import MoneyInput from '../components/MoneyInput';
import React, { useEffect, useState } from 'react';
import ConnectedAccounts from '../components/ConnectedAccounts';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import { ref, get, set, update } from 'firebase/database';
import GoalsSection from '../components/GoalsSection';
import CalendarConnectCard from '../components/CalendarConnectCard';
import ConnectYourAI from '../components/ConnectYourAI';
import NewsInterestsSection from '../components/NewsInterestsSection';
import InsuranceRecommendationsSection from '../components/InsuranceRecommendationsSection';
import { normalizeRole } from '../config/roles';
import { replayTutorial } from '../config/tutorialSteps';
import { loadProfileStatus, clearProfileStatus } from '../services/profileStatus';
import { resizeImage, formatPhone, profileFromRecord, mergeProfileForSave, commissionFromRecord, commissionToSave, displayNameFrom, capAnniversaryFromDate, capAnniversaryLabel } from '../services/profileData';
import PhoneInput from '../components/PhoneInput';
import ProfileChecklist from '../components/ProfileChecklist';
import CallingAppSetting, { canUseQuo } from '../components/CallingAppSetting';
import { showsGoogleReviewField } from '../config/roleAccess';

const page = { minHeight: '100vh', background: '#faf8f3', padding: '24px 16px', fontFamily: 'Arial, sans-serif' };
const wrap = { maxWidth: 640, margin: '0 auto' };
const card = { background: '#ffffff', border: '1px solid #d8c9a8', borderRadius: 10, padding: 20, marginBottom: 16 };
const h1 = { fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: '8px 0 4px' };
const h2 = { fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: '0 0 12px' };
const muted = { color: '#7f7f7f', fontSize: 14, margin: '0 0 12px' };
const label = { display: 'block', fontSize: 13, color: '#1a1a1a', fontWeight: 600, marginBottom: 4 };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 15, marginBottom: 12 };
const readOnly = { ...input, background: '#f5efe1', color: '#7f7f7f' };
const btn = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 15 };
const uploadBtn = { ...btn, display: 'inline-block', marginRight: 8, marginBottom: 12 };
const backBtn = { background: 'none', border: 'none', color: '#8C6D1F', cursor: 'pointer', padding: 0, fontSize: 14 };
const thumb = { width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: '2px solid #D4AF37', background: '#f5efe1', display: 'block', marginBottom: 8 };

export default function MyProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'Admin';
  const requestedUid = new URLSearchParams(location.search).get('uid');
  // Admins can open any agent's profile with ?uid=; everyone else sees only their own.
  const targetUid = requestedUid && isAdmin ? requestedUid : user?.uid;
  const isSelf = targetUid === user?.uid;
  const [commission, setCommission] = useState({ capAnniversaryMonth: '', capAnniversaryDay: '', teamSplitPercent: '', brokerSplitPercent: '', capAmount: '', agentLeadPercent: '', teamLeadPercent: '' });
  const [basicsSaved, setBasicsSaved] = useState(false);
  // Insurance picks count toward the % meter (two providers, same as the old rule).
  const [insuranceCount, setInsuranceCount] = useState(0);
  const countPicks = (v) => new Set(Array.isArray(v) ? v.filter(Boolean) : []).size;
  useEffect(() => {
    if (!targetUid) return;
    get(ref(database, `users/${targetUid}/insuranceRecommendations`)).then((snap) => setInsuranceCount(countPicks(snap.val()))).catch(() => {});
  }, [targetUid]);
  const [form, setForm] = useState({ firstName: '', lastName: '', title: '', licenseNumber: '', phone: '', contactEmail: '', websiteUrl: '', googleBusinessUrl: '', zoomLink: '', profilePhotoUrl: '', celebrationPhotoUrl: '', anniversaryDate: '', birthdate: '' });
  const [role, setRole] = useState('');
  const showAgentExtras = !['TC', 'Local Assistant', 'Marketing Manager'].includes(normalizeRole(role));
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState([]);
  const onboarding = !!(location.state && location.state.onboarding);
  useEffect(() => {
    if (!targetUid) return;
    loadProfileStatus(targetUid).then((r) => setMissing(r.missing)).catch(() => setMissing([]));
  }, [targetUid]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await get(ref(database, `users/${targetUid}`));
        const data = snap.exists() ? snap.val() : {};
        const f = profileFromRecord(data);
        if (!f.contactEmail && isSelf) f.contactEmail = user.email || '';
        // Admin accounts fall back to the team headshot until a photo is uploaded.
        if (!f.profilePhotoUrl && data.role === 'Admin') f.profilePhotoUrl = '/kelly-headshot-600.jpg';
        if (!f.celebrationPhotoUrl && data.role === 'Admin') f.celebrationPhotoUrl = '/kelly-celebration-600.jpg';
        setForm(f);
        setCommission(commissionFromRecord(data));
        setRole(data.role || '');
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Could not load your profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, targetUid]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: name === 'phone' ? formatPhone(value) : value });
  };

  const upload = async (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setError(null);
      const dataUrl = await resizeImage(file);
      setForm((f) => ({ ...f, [field]: dataUrl }));
      // Save the photo right away, so it is not lost if the agent never presses Save profile.
      await update(ref(database, `users/${targetUid}/profile`), { [field]: dataUrl });
      clearProfileStatus(targetUid);
      setMsg('Photo saved.');
    } catch (err) {
      console.error('Image upload failed:', err);
      setError('That photo could not be saved. Try a different image (JPG or PNG).');
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      setMsg(null);
      const first = form.firstName.trim();
      const last = form.lastName.trim();
      if (!first) throw new Error('Please enter your first name.');
      await update(ref(database, `users/${targetUid}`), { displayName: displayNameFrom(form) });
      const savedProfile = (await get(ref(database, `users/${targetUid}/profile`))).val() || {};
      await set(ref(database, `users/${targetUid}/profile`), mergeProfileForSave(savedProfile, form));
      if (isAdmin) {
        await set(ref(database, `users/${targetUid}/commissionSettings`), commissionToSave({ ...commission, ...capAnniversaryFromDate(form.anniversaryDate) }));
      }
      clearProfileStatus(targetUid);
      const refreshed = await loadProfileStatus(targetUid);
      setMissing(refreshed.missing);
      setMsg('Profile saved.');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={page}><div style={wrap}><p style={muted}>Loading your profile...</p></div></div>;
  }

  return (
    <div style={page}>
      <div style={wrap}>
        <button onClick={() => navigate('/dashboard')} style={backBtn}>← Back to dashboard</button>
        <h1 style={h1}>My Profile</h1>
        <button type="button" onClick={replayTutorial} style={{ background: 'none', border: 'none', padding: 0, margin: '0 0 12px', color: '#B8962E', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 }}>Replay the Resi tour</button>
        <p style={muted}>Update how you appear on shared reports and your team profile.</p>
        <p style={{ ...muted, background: '#FFF8E1', border: '1px solid #D4AF37', borderRadius: 8, padding: '10px 12px', color: '#1a1a1a' }}>
          This is your profile, not the training. Your training is under <a href="/training" style={{ color: '#8C6D1F', fontWeight: 700 }}>Training</a> in the menu.
        </p>
        {canUseQuo(user) && <CallingAppSetting user={user} />}

        {(() => {
          const keys = ['firstName', 'lastName', 'title', 'phone', 'contactEmail', 'websiteUrl', 'zoomLink', 'profilePhotoUrl', 'celebrationPhotoUrl', 'anniversaryDate', 'birthdate'].concat(showsGoogleReviewField(userRole) ? ['googleBusinessUrl'] : []);
          const filled = keys.filter((k) => String(form[k] || '').trim()).length + (showAgentExtras && insuranceCount >= 2 ? 1 : 0);
          const pct = Math.round((filled / (keys.length + (showAgentExtras ? 1 : 0))) * 100);
          return <p style={{ ...muted, fontWeight: 600 }}>Profile {pct}% complete</p>;
        })()}
        {(missing.length > 0 || basicsSaved) && (
          <ProfileChecklist uid={targetUid} onboarding={onboarding}
            onSaved={(saved) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(saved).filter(([k, v]) => v && !f[k])) }))}
            onComplete={() => { setBasicsSaved(true); setMissing([]); }} />
        )}

        <div style={card}>
          <h2 style={h2}>Photos</h2>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              {form.profilePhotoUrl ? <img src={form.profilePhotoUrl} alt="Headshot" style={thumb} /> : <div style={thumb} />}
              <label style={label}>Headshot</label>
              <label style={uploadBtn}>
                Upload headshot
                <input type="file" accept="image/*" onChange={(e) => upload(e, 'profilePhotoUrl')} style={{ display: 'none' }} />
              </label>
            </div>
            {showAgentExtras && (<div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {form.celebrationPhotoUrl ? <img src={form.celebrationPhotoUrl} alt="Celebration headshot" style={thumb} /> : <div style={thumb} />}
                <figure style={{ margin: 0, textAlign: 'center' }}>
                  <img src="/kelly-celebration-600.jpg" alt="Example: Kelly's excited celebration headshot" style={thumb} />
                  <figcaption style={{ fontSize: 12, color: '#5B6270', marginTop: 4 }}>Example</figcaption>
                </figure>
              </div>
              <label style={label}>Excited celebration headshot</label>
              <p style={{ ...muted, maxWidth: 260, margin: '0 0 8px', fontSize: 13 }}>
                To make your celebration photo, upload your headshot to ChatGPT and ask it to create an excited celebration photo of you. Then upload that image here.
              </p>
              <label style={uploadBtn}>
                Upload celebration photo
                <input type="file" accept="image/*" onChange={(e) => upload(e, 'celebrationPhotoUrl')} style={{ display: 'none' }} />
              </label>
            </div>)}
          </div>
        </div>

        {userRole === 'TC' && <CalendarConnectCard />}

        <ConnectYourAI isSelf={isSelf} isAdmin={isAdmin && isSelf} />

        <div style={card}>
          <h2 style={h2}>Your details</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={label} htmlFor="firstName">First name</label>
              <input id="firstName" name="firstName" style={input} value={form.firstName} onChange={change} />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={label} htmlFor="lastName">Last name</label>
              <input id="lastName" name="lastName" style={input} value={form.lastName} onChange={change} />
            </div>
          </div>

          <label style={label} htmlFor="title">Title</label>
          <input id="title" name="title" style={input} value={form.title} onChange={change} placeholder="e.g., Realtor®, Team Leader" />

          <label style={label} htmlFor="contactEmail">Email</label>
          <input id="contactEmail" name="contactEmail" type="email" style={input} value={form.contactEmail} onChange={change} />

          <label style={label} htmlFor="zoomLink">Zoom meeting link</label>
          <input id="zoomLink" name="zoomLink" style={input} value={form.zoomLink || ''} onChange={change} placeholder="https://zoom.us/j/..." />

          <label style={label} htmlFor="phone">Phone</label>
          <PhoneInput id="phone" name="phone" style={input} value={form.phone} onChange={change} placeholder="(239) 555-1234" />


          {showAgentExtras && (<><label style={label} htmlFor="websiteUrl">Website link</label>
          <input id="websiteUrl" name="websiteUrl" style={input} value={form.websiteUrl} onChange={change} placeholder="https://..." />

          {showsGoogleReviewField(userRole) && (<><label style={label} htmlFor="googleBusinessUrl">Google Business Profile link</label>
          <input id="googleBusinessUrl" name="googleBusinessUrl" style={input} value={form.googleBusinessUrl} onChange={change} placeholder="https://maps.google.com/..." /></>)}</>)}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {showAgentExtras && (<div style={{ flex: 1, minWidth: 180 }}>
              <label style={label} htmlFor="anniversaryDate">Realtor anniversary date</label>
              <input id="anniversaryDate" name="anniversaryDate" style={input} type="date" value={form.anniversaryDate} onChange={change} />
            </div>)}
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={label} htmlFor="birthdate">Birthdate</label>
              <input id="birthdate" name="birthdate" style={input} type="date" value={form.birthdate} onChange={change} />
            </div>
          </div>

          <label style={label}>Role</label>
          <input style={readOnly} value={role} disabled />

          {error && <p style={{ color: '#b00020', fontSize: 14 }}>{error}</p>}
          {msg && <p style={{ color: '#2e7d32', fontSize: 14 }}>{msg}</p>}
          <button onClick={save} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </div>

        {showAgentExtras && <InsuranceRecommendationsSection uid={targetUid} displayName={displayNameFrom(form)} onSaved={(chosen) => setInsuranceCount(countPicks(chosen))} />}
        {showAgentExtras && <GoalsSection uid={targetUid} />}
        <NewsInterestsSection uid={targetUid} cardStyle={card} h2Style={h2} />

        {isAdmin && (
          <div style={card}>
            <h2 style={h2}>Commission and cap (admin only)</h2>
            <p style={muted}>Only admins can see and change this section. Agents never see it.</p>
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Cap anniversary (calculated)</label>
              <div style={readOnly}>{capAnniversaryLabel(form.anniversaryDate) || 'Enter the Realtor anniversary date in your details'}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={label} htmlFor="agentLeadPercent">Agent-generated lead commission (%)</label>
                <input id="agentLeadPercent" style={input} type="number" min="0" max="100" step="0.1" value={commission.agentLeadPercent} onChange={(e) => setCommission({ ...commission, agentLeadPercent: e.target.value })} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={label} htmlFor="teamLeadPercent">Team-generated lead commission (%)</label>
                <input id="teamLeadPercent" style={input} type="number" min="0" max="100" step="0.1" value={commission.teamLeadPercent} onChange={(e) => setCommission({ ...commission, teamLeadPercent: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={label} htmlFor="brokerSplitPercent">Broker split (%)</label>
                <input id="brokerSplitPercent" style={input} type="number" min="0" max="100" step="0.1" value={commission.brokerSplitPercent} onChange={(e) => setCommission({ ...commission, brokerSplitPercent: e.target.value })} />
              </div>
            </div>
            <label style={label} htmlFor="capAmount">Company cap ($)</label>
            <MoneyInput id="capAmount" style={input} min="0" value={commission.capAmount} onChange={(e) => setCommission({ ...commission, capAmount: e.target.value })} />
            <div style={{ marginTop: 16 }}>
              <button onClick={save} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save commission and cap'}
              </button>
            </div>
          </div>
        )}
        {isSelf && <ConnectedAccounts uid={user?.uid} cardStyle={card} h2Style={h2} />}
      </div>
    </div>
  );
}
